import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Badge,
  Alert,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import TimerIcon from "@mui/icons-material/Timer";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import type { LogEntry, AircraftTiming, Placement, TowPlanResult, HangarDoor } from "../types";
import { computeExtractionOrder } from "../utils/extractionOrder";
import { DOOR_COLORS } from "./hangarCanvasUtils";

interface DecisionLogProps {
  log: LogEntry[];
  errors: string[];
  placements?: Placement[];
  /** Tow plan with collision-based blocking data (from API) */
  towPlan?: TowPlanResult;
  /** Multi-door configuration (for door column in tow sequence) */
  doors?: HangarDoor[];
}

const getLogIcon = (type: LogEntry["type"]) => {
  switch (type) {
    case "placement":
      return <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />;
    case "overlap":
      return <WarningIcon sx={{ fontSize: 16, color: "warning.main" }} />;
    case "bounds_exceeded":
      return <ErrorIcon sx={{ fontSize: 16, color: "error.main" }} />;
    case "summary":
      return <TimerIcon sx={{ fontSize: 16, color: "primary.main" }} />;
    case "info":
    default:
      return <InfoIcon sx={{ fontSize: 16, color: "info.main" }} />;
  }
};

type ChipColor = "success" | "warning" | "error" | "info" | "primary" | "secondary" | "default";

const getLogColor = (type: LogEntry["type"]): ChipColor => {
  switch (type) {
    case "placement":
      return "success";
    case "overlap":
      return "warning";
    case "bounds_exceeded":
      return "error";
    case "summary":
      return "primary";
    case "towability_fail":
      return "error";
    case "rotation_selected":
      return "info";
    case "locked":
      return "primary";
    case "orientation_selected":
      return "info";
    case "obstruction_collision":
      return "warning";
    case "info":
    default:
      return "info";
  }
};

/**
 * Get color for duration chip based on how fast it was
 */
const getDurationColor = (durationMs: number): "success" | "warning" | "error" => {
  if (durationMs < 10) return "success";
  if (durationMs < 50) return "warning";
  return "error";
};

/**
 * Format duration for display
 */
const formatDuration = (ms: number): string => {
  if (ms < 1) return `${(ms * 1000).toFixed(0)}μs`;
  if (ms < 1000) return `${ms.toFixed(2)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

/**
 * Timing Summary Table component
 */
const TimingSummaryTable: React.FC<{ details: Record<string, any> }> = ({ details }) => {
  const aircraftTimings = (details.aircraftTimings || []) as AircraftTiming[];
  const totalDurationMs = details.totalDurationMs || 0;

  if (aircraftTimings.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        Timing Breakdown (sorted by duration)
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Candidates</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Duration</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>% of Total</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aircraftTimings.map((timing, index) => {
              const pct = totalDurationMs > 0
                ? ((timing.durationMs / totalDurationMs) * 100).toFixed(1)
                : "0";
              return (
                <TableRow key={`${timing.aircraft_id}-${index}`}>
                  <TableCell>{timing.model}</TableCell>
                  <TableCell align="right">{timing.candidatesSearched}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={formatDuration(timing.durationMs)}
                      size="small"
                      color={getDurationColor(timing.durationMs)}
                      variant="outlined"
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell align="right">{pct}%</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={timing.placed ? "Placed" : "Failed"}
                      size="small"
                      color={timing.placed ? "success" : "error"}
                      variant="filled"
                      sx={{ height: 20, fontSize: 10 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ backgroundColor: "#fafafa" }}>
              <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {details.totalCandidatesSearched || 0}
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={formatDuration(totalDurationMs)}
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                />
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>100%</TableCell>
              <TableCell align="center">
                <Typography variant="caption">
                  {details.placedCount}/{details.aircraftCount}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", fontSize: 12 }}>
        <Typography variant="caption" color="text.secondary">
          Avg: <strong>{formatDuration(details.averagePlacementMs || 0)}</strong>
        </Typography>
        {details.slowestPlacement && (
          <Typography variant="caption" color="text.secondary">
            Slowest: <strong>{details.slowestPlacement.make} {details.slowestPlacement.model}</strong>
            ({formatDuration(details.slowestPlacement.durationMs)})
          </Typography>
        )}
        {details.fastestPlacement && (
          <Typography variant="caption" color="text.secondary">
            Fastest: <strong>{details.fastestPlacement.make} {details.fastestPlacement.model}</strong>
            ({formatDuration(details.fastestPlacement.durationMs)})
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const DecisionLog: React.FC<DecisionLogProps> = ({ log, errors, placements, towPlan, doors }) => {
  // Separate summary entry from regular logs
  const summaryEntry = log.find((entry) => entry.type === "summary");
  const regularLog = log.filter((entry) => entry.type !== "summary");

  // Compute extraction order from placements (using towPlan for collision-based blocking when available)
  const extractionOrder = useMemo(
    () => (placements ? computeExtractionOrder(placements, towPlan) : []),
    [placements, towPlan]
  );

  // Check for cycles in extraction order
  const hasCycle = extractionOrder.some((node) => node.extractionOrder === -1);

  // Auto-select errors tab if there are errors, otherwise timing
  const [tabIndex, setTabIndex] = useState(errors.length > 0 ? 0 : 1);

  const ErrorsTab = () => (
    <Box sx={{ pt: 1 }}>
      {errors.length > 0 ? (
        errors.map((error, index) => (
          <Box
            key={index}
            sx={{
              p: 1,
              mb: 1,
              backgroundColor: "error.light",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="error.dark">
              {error}
            </Typography>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No errors
        </Typography>
      )}
    </Box>
  );

  const TimingTab = () => (
    <Box sx={{ pt: 1 }}>
      {summaryEntry ? (
        <TimingSummaryTable details={summaryEntry.details} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No timing data
        </Typography>
      )}
    </Box>
  );

  const LogTab = () => (
    <Box sx={{ pt: 1 }}>
      {regularLog.length > 0 ? (
        regularLog.map((entry, index) => (
          <Box
            key={index}
            sx={{
              mb: 1.5,
              pb: 1.5,
              borderBottom: index < regularLog.length - 1 ? "1px solid #eee" : "none",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              {getLogIcon(entry.type)}
              <Chip
                label={entry.type}
                size="small"
                color={getLogColor(entry.type)}
                variant="outlined"
                sx={{ height: 20, fontSize: 10 }}
              />
              {entry.durationMs !== undefined && (
                <Chip
                  label={formatDuration(entry.durationMs)}
                  size="small"
                  color={getDurationColor(entry.durationMs)}
                  variant="filled"
                  sx={{ height: 20, fontSize: 10 }}
                />
              )}
              {entry.timestamp !== undefined && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                  @{formatDuration(entry.timestamp)}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" sx={{ ml: 3 }}>
              {entry.message}
            </Typography>
            {Object.keys(entry.details).length > 0 && (
              <Box
                sx={{
                  ml: 3,
                  mt: 0.5,
                  p: 1,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  fontSize: 11,
                  fontFamily: "monospace",
                }}
              >
                {Object.entries(entry.details).map(([key, value]) => (
                  <Box key={key}>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {key}:{" "}
                    </Typography>
                    <Typography component="span" variant="caption">
                      {typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No log entries
        </Typography>
      )}
    </Box>
  );

  const TowSequenceTab = () => {
    // Helper to get door info for a placement
    const getDoorInfo = (placementIndex: number) => {
      if (!placements || !doors) return null;
      const placement = placements[placementIndex];
      if (!placement?.doorId) return null;
      const door = doors.find(d => d.id === placement.doorId);
      return door ? { id: door.id, wall: door.wall } : null;
    };

    // Check if we should show the door column (only when multi-door mode is active)
    const showDoorColumn = doors && doors.length > 1;

    return (
      <Box sx={{ pt: 1 }}>
        {hasCycle && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cycle detected in blocking relationships. Some aircraft cannot be extracted.
          </Alert>
        )}
        {extractionOrder.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600, width: 60 }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Aircraft</TableCell>
                  {showDoorColumn && (
                    <TableCell sx={{ fontWeight: 600, width: 80 }}>Door</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600 }}>Blocked By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {extractionOrder
                  .slice()
                  .sort((a, b) => {
                    // Sort by extraction order, cycles (-1) at end
                    if (a.extractionOrder === -1 && b.extractionOrder === -1) return 0;
                    if (a.extractionOrder === -1) return 1;
                    if (b.extractionOrder === -1) return -1;
                    return a.extractionOrder - b.extractionOrder;
                  })
                  .map((node) => {
                    const isCycle = node.extractionOrder === -1;
                    const doorInfo = getDoorInfo(node.placementIndex);
                    return (
                      <TableRow
                        key={node.placementIndex}
                        sx={{
                          backgroundColor: isCycle ? "error.light" : "inherit",
                          "&:hover": { backgroundColor: isCycle ? "error.light" : "#f5f5f5" },
                        }}
                      >
                        <TableCell>
                          {isCycle ? (
                            <Chip
                              label="CYCLE"
                              size="small"
                              color="error"
                              sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                            />
                          ) : (
                            <Chip
                              label={`#${node.extractionOrder}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{node.model}</Typography>
                        </TableCell>
                        {showDoorColumn && (
                          <TableCell>
                            {doorInfo ? (
                              <Chip
                                label={doorInfo.wall.charAt(0).toUpperCase() + doorInfo.wall.slice(1)}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 10,
                                  backgroundColor: DOOR_COLORS[doorInfo.wall],
                                  color: "white",
                                  fontWeight: 600,
                                }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          {node.blockedBy.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              None (free)
                            </Typography>
                          ) : (
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {node.blockedBy.map((blockerId, idx) => {
                                const blocker = extractionOrder.find(
                                  (n) => n.aircraft_id === blockerId
                                );
                                return (
                                  <Chip
                                    key={`${blockerId}-${idx}`}
                                    label={blocker?.model || blockerId}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: 10 }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No placements to display
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Tabs
        value={tabIndex}
        onChange={(_, newValue) => setTabIndex(newValue)}
        sx={{ borderBottom: 1, borderColor: "divider", minHeight: 36 }}
      >
        <Tab
          label={
            errors.length > 0 ? (
              <Badge badgeContent={errors.length} color="error" sx={{ pr: 1.5 }}>
                Errors
              </Badge>
            ) : (
              "Errors"
            )
          }
          sx={{ minHeight: 36, py: 0.5, textTransform: "none" }}
        />
        <Tab label="Timing" sx={{ minHeight: 36, py: 0.5, textTransform: "none" }} />
        <Tab label="Log" sx={{ minHeight: 36, py: 0.5, textTransform: "none" }} />
        <Tab
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <LocalShippingIcon sx={{ fontSize: 16 }} />
              Tow Sequence
            </Box>
          }
          sx={{ minHeight: 36, py: 0.5, textTransform: "none" }}
        />
      </Tabs>
      {tabIndex === 0 && <ErrorsTab />}
      {tabIndex === 1 && <TimingTab />}
      {tabIndex === 2 && <LogTab />}
      {tabIndex === 3 && <TowSequenceTab />}
    </Box>
  );
};
